import React from "react";
import {
    Instagram,
    Mail,
    MessageCircle,
    CreditCard,
    Banknote,
    Wallet,
    Globe,
    ArrowRight,
    MapPin,
} from "lucide-react";
import {
    LOGO_URL,
    WHATSAPP_URL,
    CONTACT_EMAIL,
    INSTAGRAM_URL,
    P_IVA,
    APP_GOOGLE,
    APP_APPLE,
    COACHPLUS_URL,
    MAPS_URL,
} from "../data/site";

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="relative border-t border-white/5 bg-ink"
        >
            <div className="container mx-auto px-4 sm:px-6 lg:px-10 py-14">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-3 mb-4">
                            <img
                                src={LOGO_URL}
                                alt="RPF Coaching"
                                className="h-12 w-12 rounded-md ring-1 ring-gold/30 object-cover"
                            />
                            <div>
                                <div className="font-oswald font-bold text-xl text-white tracking-widest">
                                    RPF
                                </div>
                                <div className="font-oswald text-[10px] tracking-[0.35em] text-gold">
                                    COACHING
                                </div>
                            </div>
                        </div>
                        <p className="font-manrope text-sm text-zinc-400 max-w-md leading-relaxed">
                            Team di coaching online & in presenza dedicato a bodybuilding,
                            calisthenics e nutrizione sportiva. Fondato da Manuel Romano.
                        </p>

                        {/* Payment methods */}
                        <div className="mt-6">
                            <div className="font-manrope text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
                                Metodi di pagamento
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                {[
                                    { i: CreditCard, l: "Carta" },
                                    { i: Banknote, l: "Bonifico" },
                                    { i: Wallet, l: "Contanti" },
                                ].map((m) => {
                                    const I = m.i;
                                    return (
                                        <span
                                            key={m.l}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-ink-surface border border-white/10 font-oswald uppercase text-[10px] tracking-widest text-zinc-300"
                                        >
                                            <I size={12} className="text-gold" />
                                            {m.l}
                                        </span>
                                    );
                                })}
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gold/10 border border-gold/30 font-oswald uppercase text-[10px] tracking-widest text-gold">
                                    Pagamenti a rate
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-3">
                        <div className="font-oswald font-bold uppercase text-white tracking-widest text-sm mb-4">
                            Esplora
                        </div>
                        <ul className="space-y-2 font-manrope text-sm text-zinc-400">
                            <li><a href="#chi-siamo" className="hover:text-gold transition-colors">Chi Siamo</a></li>
                            <li><a href="#servizi" className="hover:text-gold transition-colors">Servizi</a></li>
                            <li><a href="#pacchetti" className="hover:text-gold transition-colors">Pacchetti</a></li>
                            <li><a href="#metodo" className="hover:text-gold transition-colors">Metodo</a></li>
                            <li><a href="#team" className="hover:text-gold transition-colors">Team</a></li>
                            <li><a href="#app" className="hover:text-gold transition-colors">App</a></li>
                            <li><a href="#contatti" className="hover:text-gold transition-colors">Contatti</a></li>
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <div className="font-oswald font-bold uppercase text-white tracking-widest text-sm mb-4">
                            Contatti
                        </div>
                        <ul className="space-y-3 font-manrope text-sm text-zinc-400">
                            <li className="flex items-center gap-2">
                                <MessageCircle size={14} className="text-gold" />
                                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="hover:text-gold transition-colors">+39 329 464 6429</a>
                            </li>
                            <li className="flex items-center gap-2">
                                <Mail size={14} className="text-gold" />
                                <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-gold transition-colors break-all">{CONTACT_EMAIL}</a>
                            </li>
                            <li className="flex items-center gap-2">
                                <Instagram size={14} className="text-gold" />
                                <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="hover:text-gold transition-colors">@rpf_coaching_</a>
                            </li>
                            <li className="flex items-center gap-2">
                                <MapPin size={14} className="text-gold flex-shrink-0" />
                                <a href={MAPS_URL} target="_blank" rel="noopener" className="hover:text-gold transition-colors">Via Alessandro Manzoni 204/A, San Giorgio a Cremano (NA)</a>
                            </li>
                        </ul>

                        <div className="mt-6 flex flex-wrap gap-2">
                            <a
                                href={APP_APPLE}
                                target="_blank"
                                rel="noreferrer"
                                className="px-3 py-1.5 rounded-md bg-ink-surface border border-white/10 font-oswald uppercase text-[10px] tracking-widest text-zinc-300 hover:text-gold hover:border-gold/40 transition-colors"
                            >
                                App Store
                            </a>
                            <a
                                href={APP_GOOGLE}
                                target="_blank"
                                rel="noreferrer"
                                className="px-3 py-1.5 rounded-md bg-ink-surface border border-white/10 font-oswald uppercase text-[10px] tracking-widest text-zinc-300 hover:text-gold hover:border-gold/40 transition-colors"
                            >
                                Google Play
                            </a>
                        </div>
                    </div>
                </div>

                <a
                    href={COACHPLUS_URL}
                    target="_blank"
                    rel="noopener"
                    data-testid="footer-coachplus"
                    className="mt-12 group relative overflow-hidden flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 sm:p-6 rounded-xl bg-gradient-to-r from-gold/10 via-gold/5 to-transparent border border-gold/30 hover:border-gold transition-all"
                >
                    <div className="flex items-center gap-4">
                        <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-gold/15 border border-gold/40 flex items-center justify-center">
                            <Globe size={20} className="text-gold" />
                        </div>
                        <div>
                            <div className="font-manrope text-[11px] uppercase tracking-[0.3em] text-zinc-400">
                                Storefront ufficiale
                            </div>
                            <div className="font-oswald font-bold text-white text-lg uppercase tracking-wide mt-0.5">
                                Sito Home MR Coaching
                            </div>
                        </div>
                    </div>
                    <div className="inline-flex items-center gap-2 bg-gold text-ink font-oswald font-bold uppercase tracking-wider px-5 py-2.5 rounded text-sm group-hover:bg-gold-bright group-hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] transition-all">
                        Vai al sito
                        <ArrowRight
                            size={14}
                            className="transition-transform group-hover:translate-x-1"
                        />
                    </div>
                </a>

                <div className="mt-8 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="font-manrope text-xs text-zinc-500 text-center sm:text-left">
                        Copyright © {new Date().getFullYear()} <span className="text-zinc-300">Manuel Romano</span> — P.IVA {P_IVA}
                    </div>
                    <div className="font-manrope text-[11px] text-zinc-600 uppercase tracking-[0.2em]">
                        Coaching online & in presenza
                    </div>
                </div>
            </div>
        </footer>
    );
}
